import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts';
import { TeamProvider } from './contexts/TeamContext';
import Layout from './components/Layout';
import LandingPage from './components/LandingPage';
import { LoginForm, RegisterForm, ResetPasswordForm, EmailVerification, TokenVerificationForm } from './components/Auth';
import { LLMAPITestPage } from './pages/LLMAPITestPage';
import ClientIntelligence from './pages/ClientIntelligence';
import NeuralTemplatePage from './pages/NeuralTemplatePage';
import NotebookPage from './pages/NotebookPage';
import SettingsPage from './pages/SettingsPage';
import AcceptInvitationPage from './pages/AcceptInvitationPage';
import TeamTranscriptsPage from './pages/TeamTranscriptsPage';
import { useAuth } from './contexts';

// Loading component
const LoadingScreen: React.FC = () => (
  <div className="fixed inset-0 bg-white flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-accent-purple"></div>
  </div>
);

// Protected Route wrapper component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  // Show loading screen during initial authentication check
  if (isLoading) {
    return <LoadingScreen />;
  }
  
  // Only redirect if we know for certain user is not authenticated
  if (!isAuthenticated) {
    // Store the current location for redirect after login
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

// Public Route wrapper component
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  // Show loading screen during initial authentication check
  if (isLoading) {
    return <LoadingScreen />;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  // Track app performance for critical issues only
  React.useEffect(() => {
    // Track when window is fully loaded for performance monitoring
    window.addEventListener('load', () => {
      // Only log if load time is unusually slow (>2 seconds)
      const loadTime = performance.now();
      if (loadTime > 2000) {
        console.warn(`⚠️ Slow app load: ${Math.round(loadTime)}ms`);
      }
    });

    // Performance observer for navigation timing - only log if slow
    if ('PerformanceObserver' in window) {
      try {
        const observer = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.entryType === 'navigation') {
              const navEntry = entry as PerformanceNavigationTiming;
              const totalTime = navEntry.loadEventEnd - navEntry.fetchStart;
              // Only log if navigation is slow (>3 seconds)
              if (totalTime > 3000) {
                console.warn('⚠️ Slow navigation timing:', {
                  domContentLoaded: Math.round(navEntry.domContentLoadedEventEnd - navEntry.domContentLoadedEventStart),
                  loadComplete: Math.round(navEntry.loadEventEnd - navEntry.loadEventStart),
                  totalTime: Math.round(totalTime)
                });
              }
            }
          }
        });
        observer.observe({ entryTypes: ['navigation'] });
      } catch (e) {
        // Performance Observer not supported - no logging needed
      }
    }
  }, []);

  return (
    <AuthProvider>
      <TeamProvider>
        <Routes>
          {/* Public Landing Page */}
          <Route path="/" element={<LandingPage />} />

          {/* Auth Routes */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <LoginForm />
              </PublicRoute>
            }
          />
          <Route
            path="/register"
            element={
              <PublicRoute>
                <RegisterForm />
              </PublicRoute>
            }
          />
          <Route
            path="/reset-password"
            element={
              <PublicRoute>
                <ResetPasswordForm />
              </PublicRoute>
            }
          />
          <Route path="/verify-email" element={<EmailVerification />} />
          <Route
            path="/verify-token"
            element={
              <PublicRoute>
                <TokenVerificationForm
                  onVerificationSuccess={(user) => {
                    // Navigation is handled by the AuthContext
                  }}
                  onBackToLogin={() => window.location.href = '/login'}
                />
              </PublicRoute>
            }
          />

          {/* Team Invitation Route */}
          <Route path="/accept-invitation" element={<AcceptInvitationPage />} />

          {/* Protected Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tasks"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          />
          <Route
            path="/clients"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          />

          <Route
            path="/von-chat"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          />
          <Route
            path="/transcript/:id"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          />
          <Route
            path="/test-llmapi"
            element={
              <ProtectedRoute>
                <LLMAPITestPage />
              </ProtectedRoute>
            }
          />
          {/* Redirect global-insights to home - feature not ready */}
          <Route
            path="/global-insights"
            element={<Navigate to="/" replace />}
          />
          <Route
            path="/client-intelligence"
            element={
              <ProtectedRoute>
                <ClientIntelligence />
              </ProtectedRoute>
            }
          />
          <Route
            path="/neural-templates"
            element={
              <ProtectedRoute>
                <NeuralTemplatePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/notemann"
            element={
              <ProtectedRoute>
                <NotebookPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/teams/:teamId/transcripts"
            element={
              <ProtectedRoute>
                <TeamTranscriptsPage />
              </ProtectedRoute>
            }
          />
        </Routes>
      </TeamProvider>
    </AuthProvider>
  );
};

export default App; 